<template>
    <USlideover
        v-if="available && everOpened"
        v-model:open="open"
        side="right"
        :modal="false"
        :overlay="false"
        :dismissible="false"
        :unmount-on-hide="false"
        :close="false"
        :title="$t('assistant.title')"
        :description="$t('assistant.intro')"
        :ui="{ content: 'max-w-full sm:max-w-[440px]' }"
    >
        <template #content>
            <div class="flex h-full flex-col" @keydown.escape="closeDrawer">
                <!-- The header is Milo himself, then the thread's name, then the
                     three things you do to a thread: start another, take it to the
                     full page, put it away.

                     The head is 96 px and the name is beside it rather than under it:
                     a portrait wants a name, and a 440 px panel cannot spend a third
                     of its height on a header. What is BOLD is „Milo“ and the thread's
                     own title is the line under it — the opposite of the small orb's
                     header, and right for this one, because at this size the face is
                     what the eye lands on and the name is what it reads. -->
                <div class="flex items-center gap-3 border-b border-default px-3 py-2">
                    <MiloHead :state="orbState" :size="96" decorative />

                    <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-semibold text-highlighted">{{ $t('assistant.title') }}</p>
                        <p class="truncate text-xs text-muted">
                            {{ conversation?.conversationTitle ?? $t('assistant.new_conversation') }}
                        </p>
                    </div>

                    <UButton
                        icon="i-lucide-plus"
                        color="neutral"
                        variant="ghost"
                        size="sm"
                        :aria-label="$t('assistant.new_conversation')"
                        :title="$t('assistant.new_conversation')"
                        @click="conversation?.startNew()"
                    />

                    <UButton
                        icon="i-lucide-maximize-2"
                        color="neutral"
                        variant="ghost"
                        size="sm"
                        :aria-label="$t('assistant.open_full')"
                        :title="$t('assistant.open_full')"
                        @click="openFullScreen"
                    />

                    <UButton
                        icon="i-lucide-x"
                        color="neutral"
                        variant="ghost"
                        size="sm"
                        :aria-label="$t('general.close')"
                        :title="`${$t('general.close')} (Esc)`"
                        @click="closeDrawer"
                    />
                </div>

                <div class="flex min-h-0 flex-1 flex-col p-3">
                    <AssistantConversation ref="conversation" :show-history="false" :visible="open" />
                </div>
            </div>
        </template>
    </USlideover>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
    import { useRouter } from 'vue-router'

    import AssistantConversation from '@/components/AssistantConversation.vue'
    import MiloHead from '@/components/app/MiloHead.vue'
    import { useAssistant } from '@/libs/assistant'
    import { useAppStore } from '@/stores/app'

    /**
     * The assistant beside the page rather than instead of it.
     *
     * # Non-modal, deliberately
     *
     * No overlay, `modal: false`, and `dismissible: false` — so the page behind stays
     * readable, scrollable and clickable while the drawer is open. That is the whole
     * reason it exists: the desk asks about the booking that is on the screen, reads
     * the proposal beside it, and presses Potrdi with the booking still in front of
     * them. A dimmed page you cannot touch would be the full page with extra steps.
     *
     * `dismissible: false` is what buys that. Nuxt UI's dismissible closes an overlay
     * on any outside pointer press, which for a non-modal panel means it shuts the
     * moment you touch the thing you opened it to talk about. Escape still closes it,
     * from the drawer's own keydown handler — scoped to the panel, so a dialog opened
     * on top of the page keeps its own Escape.
     *
     * # Kept alive, not rebuilt
     *
     * `unmount-on-hide: false`, so closing the drawer does not throw the conversation
     * away: the same thread is there when it is opened again a screen later, which is
     * exactly the continuity a drawer is for. It is not RENDERED until the first open
     * (`everOpened`), so an account that never touches it pays nothing for it.
     *
     * # 440 px, and the whole width on a telephone
     *
     * The house theme already gives a right-hand slideover `w-full max-w-md` and
     * `inset-y-0`, so the panel is full height and 448 px wide out of the box. Only the
     * ceiling is overridden, and it is `max-w-full` at the base rather than `w-full`:
     * tailwind-merge replaces a class with one of the same kind at the same breakpoint,
     * so `w-full` would have left `max-w-md` standing and a phone would have got a
     * 448 px panel with a strip of page beside it.
     *
     * # The title nobody sees
     *
     * `title` and `description` are set although the header below draws neither of
     * them. Nuxt UI puts them in a visually-hidden `DialogTitle`/`DialogDescription`
     * whenever the `content` slot is used — which is what gives the panel an
     * accessible name. Without them a screen reader announces an unnamed dialog.
     *
     * # Where the state lives
     *
     * `libs/assistant.ts`: open/closed and the last thread, both per device in
     * localStorage. This component only draws it.
     */

    const router   = useRouter()
    const appStore = useAppStore()

    const { open, threadId, orbState, openDrawer, closeDrawer } = useAssistant()

    const conversation = useTemplateRef<InstanceType<typeof AssistantConversation>>('conversation')

    /** No permission, no assistant — and no drawer, not a disabled one. */
    const available = computed((): boolean => appStore.can('chat:use'))

    const everOpened = ref(open.value)

    watch(open, opened => { if (opened) everOpened.value = true }, { immediate: true })

    /** Which thread this device was last on, remembered so that closing the drawer and
     *  coming back an hour later is the same conversation. */
    watch(() => conversation.value?.conversationId, id => { if (id) threadId.value = id })

    // The first render is the first open, so the restore happens here rather than on
    // mount: there is nothing to restore into until the conversation exists.
    watch(conversation, async instance => {
        if (!instance || !threadId.value) return

        // A thread the device may no longer open — gone, or another person's — is
        // forgotten, so the drawer opens on a fresh conversation and not on somebody
        // else's answers marked read-only.
        if (!await instance.loadConversation(threadId.value, true)) threadId.value = ''
    })

    const openFullScreen = () => {
        const id = conversation.value?.conversationId

        closeDrawer()
        void router.push({ name: 'assistant', query: id ? { conversation: id } : {} })
    }

    /**
     * Alt+J opens and closes it.
     *
     * Alt because every unmodified letter on the chessboard is already a shortcut
     * (Chessboard.vue: n, p, o, f, /, 1-3), and that handler returns immediately on a
     * modifier — so this cannot collide with it. J for nothing better than that it is
     * on the home row, unclaimed by the browsers this runs in, and not a letter any
     * screen here uses with Alt. The tooltip on the orb names it.
     *
     * Matched on `code` and not on `key`. Alt is a COMPOSING modifier on macOS: Alt+J
     * arrives with `key` set to "∆", so a handler reading `key` would work on Windows
     * and do nothing at all on a Mac. `code` is the physical key and is also what keeps
     * this working on the Slovenian layout. `key` stays as the fallback for a browser
     * that reports no code.
     */
    const onKeydown = (event: KeyboardEvent) => {
        if (!event.altKey || event.ctrlKey || event.metaKey) return
        if (event.code ? event.code !== 'KeyJ' : event.key.toLowerCase() !== 'j') return
        if (!available.value) return

        event.preventDefault()

        if (open.value) closeDrawer()
        else openDrawer()
    }

    onMounted(() => window.addEventListener('keydown', onKeydown))
    onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>
